<template>
    <b-card no-body class="m-1">
        <!-- Titre de la question -->
        <b-card-sub-title :sub-title="question" class="font-weight-bold mb-1 pl-1" />

        <!-- Groupe de boutons radio -->
        <b-button-group class="d-flex justify-content-center btn-group-sm w-100">
            <b-form-radio-group v-model="selectedValue"
                                buttons
                                class="d-flex flex-wrap justify-content-center w-100"
                                @change="updateValue">
                <b-form-radio v-for="option in ratingOptions"
                              :key="option.value"
                              :value="option.value"
                              :button-variant="getOutlineColor(option.value)"
                              button
                              class="btn rounded text-center btn-sm px-2 mx-1">
                    {{ option.value }}
                </b-form-radio>
            </b-form-radio-group>
        </b-button-group>

        <!-- Labels -->
        <b-row class="text-left mt-1 px-2">
            <b-col>
                <small class="text-muted text-start font-weight-bold">
                    {{ $t('NotAtAllSatisfied') }}
                </small>
                <!-- Pas du tout satisfait -->
            </b-col>
            <b-col class="text-right">
                <small class="text-muted text-end font-weight-bold">
                    {{ $t('VerySatisfied') }}
                </small>
                <!-- Très satisfait -->
            </b-col>
        </b-row>
        <!-- Champ de commentaire facultatif -->
        <div v-if="hasRemark" class="mt-3">
            <!-- Label du champ -->
            <b-card-sub-title class="font-weight-bold mb-1 pl-1" :sub-title="$t('OptionalComment')" />
            <!-- Zone de texte pour saisie libre -->
            <b-form-textarea v-model="remark"
                             :placeholder="$t('PlaceHolderComment')"
                             rows="3"
                             :state="remarkState"
                             @keydown="handleKeyDown"
                             @input="updateValue" />
                <b-form-invalid-feedback :state="remarkState">
                    {{ $t('MaxLengthReached', { '0': maxLength }) }}
				</b-form-invalid-feedback>

            <!-- Affichage du nombre de caractères -->
            <small class="text-muted d-block text-right">
                {{ remark.length }} / {{maxLength}}
            </small>

        </div>
        <div class="w-100 bg-white my-2" style="height: 2px;"></div>
    </b-card>
</template>

<script lang="ts">

    import { Component, Prop, Vue } from "vue-property-decorator";
    import locales from '../locales/Question.json';

    /**
     * Composant enfant de SatisfactionSurvey responsable de mapper les questions
     * et d'émettre la valeur sélectionnée.
     */
    @Component({
        i18n: { messages: locales }
    })
    export default class QuestionSurvey extends Vue {
        /** Label (titre) de la question */
        @Prop({ required: true })
        public question!: string;

        /** Identifiant numérique de la question */
        @Prop({ required: true })
        public questionRef!: number;

        /** Si l'utilisateur a la possibilité d'ajouter une remarque à la réponse */
        @Prop({ required: true })
        public hasRemark: Boolean;

        /** Note sélectionnée (peut être nulle si l'utilisateur n'a pas encore répondu). */
        @Prop({ default: null })
        private initialValue!: number | null;

        /** Remarque facultative, reçue éventuellement du parent. */
        @Prop({ default: "" })
        private initialRemark!: string;

        /* Valeur interne pour la note sélectionnée. */
        private selectedValue?: number = null;

        /* Valeur interne pour la remarque saisie.*/
        private remark: string = "";

        /** Valeur Maximum de caractères inclus dans la remarque*/
        private readonly maxLength = 256;

        /**  Génération du formulaire de notation pour chaque question On va de 1 à 10 inclus (10 valeurs) */
        private ratingOptions = Array.from({ length: 10 }, (_, i) => ({
            value: i + 1,  // Commencer à 1 au lieu de 0
            text: (i + 1).toString(),
        }));

        /* Au montage du composant, on initialise l'état interne à partir des props, si besoin. */
        mounted() {
            this.selectedValue = this.initialValue;
            this.remark = this.initialRemark;
        }

        /**
         * Détermine la variante des boutons radio pour avoir
         * l'intérieur blanc et la bordure colorée (« outline-... »).
         *
         * @param value - la valeur sélectionnée
         * @returns string - la classe Bootstrap "outline-danger", "outline-warning" ou "outline-success"
         */
        private getOutlineColor(value: number): string {
            // 0 à 6 : contour rouge
            if (value <= 6) return "outline-danger";
            // 7 à 8 : contour orange
            if (value <= 8) return "outline-warning";
            // 9 à 10 : contour vert
            return "outline-success";
        }

        /* Émet la mise à jour vers le parent sous forme d'objet contenant { replyValue, remark }. */
        private updateValue(): void {
            this.$emit("input", {
                questionRef: this.questionRef,
                replyValue: this.selectedValue ?? null,
                remark: this.remark ?? "",
            });
        }

        /** Getter qui détermine l'état de validation du textarea
         Getter qui détermine l'état de validation du textarea
        Retourne :
         - false si la limite de caractères est dépassée (affiche un état d'erreur)
         - null si le texte est valide (aucun état particulier) */
        get remarkState(): boolean | null {
            return this.remark.length > this.maxLength ? false : null;
        }

        /** Gère les événements clavier pour empêcher la saisie au-delà de la limite */
        private handleKeyDown(e: KeyboardEvent) {
            /**  Liste des touches qui doivent toujours fonctionner */
            const allowedKeys = [
                'Backspace', 'Delete', // Suppression
                'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', // Navigation
                'Home', 'End' // Début/Fin
            ];

            /** Conditions pour bloquer la saisie : lorsque nombre de caractères max est atteint
             1. La limite est atteinte ou dépassée
             2. La touche pressée n'est pas dans les touches autorisées
             3. Ce n'est pas une touche de contrôle (Ctrl/Cmd) */
            if (this.remark.length >= this.maxLength &&
                !allowedKeys.includes(e.key) &&
                !e.ctrlKey && !e.metaKey) {
                e.preventDefault(); // Empêche l'action par défaut
            }
        }
    }


</script>